import { ActivatedRoute, Router } from '@angular/router';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  Inject,
} from '@angular/core';
import {
  Chip,
  DropdownComponent,
  ResponsiveService,
  SearchComponent,
} from '@frk/eds-components';
import { FilterType, FiltersState, OptionWithCount } from '@types';

import { FundListingComponentConfig } from '../fund-listing-component.config';
import { Location } from '@angular/common';
import { Logger } from '@utils/logger';
import { Menu } from '@bloomreach/spa-sdk';
import { ProductFilterStateManagerService } from '../services/product-filter-state-manager.service';
import { SiteConfigService } from '@services/site-config.service';
import { TranslateService } from '@shared/translate/translate.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { WINDOW } from '@ng-web-apis/common';

const logger = Logger.getLogger('FundFilterComponent');

interface ProductTypeOption {
  label: string;
  value: string;
}

export type FilterChip = Chip & {
  key: string;
};

interface SelectedFiltersArray {
  selectedFilters: FilterChip[];
}

type DropDownMultiSelectItem = OptionWithCount & {
  checked: boolean;
};

type DropDownMultiSelect = FilterType & {
  preSelectedValues: OptionWithCount[];
};

@Component({
  selector: 'ft-fund-filters',
  templateUrl: './fund-filters.component.html',
  styleUrls: ['./fund-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundFiltersComponent implements OnInit, OnDestroy {
  @Input() componentConfiguration: FundListingComponentConfig;
  @Input() menu: Menu;
  state: FiltersState;
  selectedFilterLabels: SelectedFiltersArray[] = [];
  productTypeOptions: ProductTypeOption[] = [];
  preSelectedValue: ProductTypeOption;
  filterSelected: boolean;
  filterCount: number;
  additionalFiltersCount: number;
  isSearchText = false;
  isMobile$: Observable<boolean>;
  isHandheld$: Observable<boolean>;
  private searchSubject: Subject<string> = new Subject();
  searchSubscription: Subscription;
  chipRemoveUrl: string;

  @ViewChildren('filterSelection') filterSelection: QueryList<ElementRef>;
  @ViewChild('filterSearch') filterSearch: SearchComponent;
  @ViewChildren('filtersDropDowns')
  filtersDropDowns: QueryList<DropdownComponent>;

  constructor(
    private filterStateManagerService: ProductFilterStateManagerService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private siteConfigService: SiteConfigService,
    private translateService: TranslateService,
    private responsiveService: ResponsiveService,
    @Inject(WINDOW) readonly windowRef: Window
  ) {
    this.isMobile$ = this.responsiveService.isMobile$();
    this.isHandheld$ = this.responsiveService.isHandheld$();
  }

  ngOnInit(): void {
    if (this.menu) {
      this.mapProductTypeOptions();
    }

    // Get filter count for skeleton loader
    this.filterCount = this.siteConfigService.product?.ppss.primaryFilters[
      this.componentConfiguration?.configurationName
    ]?.length;

    this.additionalFiltersCount = this.siteConfigService.product?.ppss.additionalFilters[
      this.componentConfiguration?.configurationName
    ]?.length;

    this.filterStateManagerService.subscribe((state: FiltersState) => {
      this.state = state;
      this.mapPrimaryAndAdditionalFilterOptions();

      this.selectedFilterLabels = this.fetchSelectedFilters(this.state);
      this.cdr.detectChanges();
      logger.debug(this.state);
      this.updateUrl();
    });

    const urlParam = this.route?.snapshot?.queryParams?.filter;
    if (urlParam) {
      this.filterStateManagerService.mapPreFilterState(
        this.componentConfiguration,
        urlParam
      );
    } else {
      this.filterStateManagerService.mapFilterState(
        this.componentConfiguration
      );
    }

    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(500))
      .subscribe((searchedValue) => {
        this.filterStateManagerService.searchByKeyword(searchedValue);
        this.updateResetBtn(searchedValue);
      });
  }

  /**
   * Map product type options only.
   */
  private mapProductTypeOptions() {
    this.productTypeOptions = this.menu.siteMenuItems.map((item) => ({
      label: item.name,
      value: item._links.site.href,
    }));
    this.preSelectedValue = this.productTypeOptions.find((item) =>
      this.router.isActive(item.value, false)
    );
  }

  /**
   * Map primary & additional type filter options.
   */
  private mapPrimaryAndAdditionalFilterOptions() {
    const filters = [
      ...this.state.primaryFilters,
      ...this.state.additionalFilters,
    ];

    filters.forEach((filterType: DropDownMultiSelect) => {
      if (filterType.options.length > 0) {
        filterType.options.forEach((optionProps: DropDownMultiSelectItem) => {
          optionProps.label =
            filterType.label === 'SFDR'
              ? `${this.translateService.instant(
                  'products.filter-sfdr-label-article'
                )} ${optionProps.value}`
              : optionProps.value;
          optionProps.checked = filterType.selected.includes(optionProps.value)
            ? true
            : false;
        });
      }
    });

    filters.forEach((filterType: DropDownMultiSelect) => {
      filterType.preSelectedValues = filterType.options.filter(
        (optionProps: DropDownMultiSelectItem) => optionProps.checked
      );
    });
  }

  /**
   * Fetch the state and parse the selected filters.
   * Return the cumulative filter labels applied.
   * @param state FundFiltersState object
   */
  private fetchSelectedFilters(state: FiltersState): SelectedFiltersArray[] {
    const filterLabels: SelectedFiltersArray[] = [];
    let filterTypeLables: FilterChip[];

    if (state.searchKeyword.selected?.length > 0) {
      this.isSearchText = true;
    }
    const filters: FilterType[] = [
      ...state.primaryFilters,
      ...state.additionalFilters,
    ];

    filters.forEach((filter: FilterType) => {
      if (filter.selected.length > 0) {
        filterTypeLables = [];
        filter.selected.forEach((selectedValue: string) => {
          filterTypeLables.push({
            key: filter.label,
            label: this.translateService.instant(
              `products.ppss-filter-${filter.label}`
            ),
            value: selectedValue,
          });
        });
        filterLabels.push({ selectedFilters: filterTypeLables });
      }
    });

    return filterLabels;
  }

  /**
   * Handle product type selection.
   * @param event event triggered upon value selection
   */
  selectProduct(event: any) {
    this.router.navigate([event.value], { queryParamsHandling: 'preserve' });
    // Preserve UTM query parameters for Analytics tracking
  }

  /**
   * Handle selected filter values.
   * Send filter label and its values to filter state service to filter out the rows.
   * @param filterClass the filter class with either of the values from 'primary'/'additional'.
   * @param selection event triggered upon value selection
   */
  updateFilter(
    filterClass: string,
    selectedLabel: string,
    selection: DropDownMultiSelectItem[]
  ): void {
    if (selection.length > 0) {
      const selectedValues: string[] = [];

      let iterator = 0;
      while (iterator < selection.length) {
        selectedValues.push(selection[iterator].value);
        iterator++;
      }

      this.filterSelected = true;
      setTimeout(() => {
        this.filterStateManagerService.updateFilter(
          filterClass,
          selectedLabel,
          selectedValues
        );
      }, 0);
    } else {
      this.filterSelected = false;
      setTimeout(() => {
        this.filterStateManagerService.updateFilter(
          filterClass,
          selectedLabel,
          []
        );
      }, 0);
    }
  }

  /**
   * Handle text input on filter search box.
   * @param event event triggered upon value entered
   */
  searchByKeyword(value: string): void {
    this.searchSubject.next(value);
  }

  /**
   * Reset the selection and hence table rows.
   */
  resetFilters(event) {
    event?.preventDefault();
    this.filterSearch.value = '';
    this.filterSearch.hasTyped = false;
    this.filterSelected = false;
    this.isSearchText = false;
    this.filterStateManagerService.resetFilters();
    if (this.filtersDropDowns) {
      this.filtersDropDowns.forEach((dropDown) => {
        dropDown.reset();
      });
    } else {
      logger.error('filter dropdowns: ', this.filtersDropDowns);
    }
    logger.event('clicked on reset filters');
  }

  /**
   * Remove the selected filter.
   * @param event event triggered upon removing the selected filter from Chip
   */
  removeFilter(event: FilterChip) {
    const key = event.key;
    const value = event.value;
    let filterClass: string;
    let filterTypeToBeRemoved: FilterType;

    this.state.primaryFilters.find((filter: FilterType) => {
      if (filter.type.key === key) {
        filterTypeToBeRemoved = filter;
        filterClass = 'primary';
      }
    });
    this.state.additionalFilters.find((filter: FilterType) => {
      if (filter.type.key === key) {
        filterTypeToBeRemoved = filter;
        filterClass = 'additional';
      }
    });

    const indexOfValue = filterTypeToBeRemoved.selected.indexOf(value);

    if (this.filtersDropDowns) {
      const valueToBeremove = filterTypeToBeRemoved.selected[indexOfValue];
      this.filtersDropDowns.forEach((dropDown) => {
        if (dropDown.id === key) {
          dropDown.getSelected({
            value: valueToBeremove,
            label: value,
            checked: false,
          });
        }
      });
    } else {
      logger.error('filter dropdowns: ', this.filtersDropDowns);
    }
    logger.event('clicked on remove Chip of ', value);
  }

  updateUrl() {
    const selected = this.filterStateManagerService.getSelectedFilters();
    const updateUrl = this.router
      .createUrlTree([], {
        relativeTo: this.route,
        queryParams: selected ? { filter: selected } : null,
        queryParamsHandling: 'merge',
      })
      .toString();
    this.location.go(updateUrl);
    this.chipRemoveUrl = this.windowRef.location.href;
  }

  updateResetBtn(value: string) {
    if (value.length > 0) {
      this.isSearchText = true;
    } else {
      this.isSearchText = false;
    }
  }

  ngOnDestroy(): void {
    this.filterStateManagerService.unsubscribe();
    this.searchSubscription.unsubscribe();
  }
}
